import React, { useEffect, useState } from 'react';
import { CreditCards } from 'src/components/credit-cards';
import { Currency } from 'src/components/currency';
import { InstallmentsCalculatorCard } from 'src/components/installments-calculator-card';
import { InstallmentsInput } from 'src/components/installments-input';
import { LearnMoreHowItWorks } from 'src/components/learn-more-how-it-works';
import { InstallmentItem } from 'src/components/long-term-content/components/installment-item';
import { PayNextMonthTab } from 'src/components/pay-next-month-tab';
import { PaymentOptionsList } from 'src/components/payment-options-list';
import { PopupLearnMoreBackgroundHeader } from 'src/components/popup-learn-more-background-header';
import { TrustBanner } from 'src/components/trust-banner';
import { Button } from 'src/components/v2/button';
import { ACTIONS } from 'src/constants/common';
import { COUNTRY_BY_CURRENCY_MAP } from 'src/constants/country-by-currency-map';
import { INITIAL_PRICE, INSTALLMENT_PLANS } from 'src/constants/learn-more';
import { useAppContext } from 'src/context/store/app-provider';
import { useIsRamadanDefaultViewExp } from 'src/hooks/use-is-ramadan-exp';
import { useLocale } from 'src/hooks/use-locale';
import { useNextBillingDate } from 'src/hooks/use-next-billing-date';
import { createNormalizeCheckingNumber } from 'src/utils/create-normalize-checking-number';
import { getLearnMoreBackgroundTitle } from 'src/utils/get-learn-more-background-title';
import { getPaymentText } from 'src/utils/get-payment-text';
import { getPriceSplitOf } from 'src/utils/get-price-split-of';
import { getSplitYourPurchaseText } from 'src/utils/get-split-purchase-text';
import { useGetPaymentScheduleTitles } from 'src/utils/use-get-payment-schedule-titles';

import { DEFAULT_RAMADAN_INSTALLMENTS_COUNT } from '../default-promo-widget/components/non-ltf-snippet/non-ltf-snippet';
import { getLearnMoreWithScheduleDisplayState } from './get-learn-more-with-schedule-display-state';
import styles from './LearnMoreWithSchedule.css';

interface Props {
  onClose?: () => void;
  onContinue?: () => void;
}

export const LearnMoreWithSchedule = ({ onClose, onContinue }: Props) => {
  const {
    price,
    priceRaw,
    currency,
    installmentsCount: defaultInstallmentsCount,
    dispatch,
    partnerCharges,
    offers,
  } = useAppContext();
  const country = COUNTRY_BY_CURRENCY_MAP[currency];
  const hasInstallments = Boolean(offers.installments?.length);
  const billingDate = useNextBillingDate();
  const { shouldUsePayLaterHeader, shouldRenderInstallmentsSchedule } =
    getLearnMoreWithScheduleDisplayState({
      hasInstallments,
      billingDate,
    });
  const isRamadanDefaultViewExp = useIsRamadanDefaultViewExp();
  const installmentsCount = isRamadanDefaultViewExp
    ? DEFAULT_RAMADAN_INSTALLMENTS_COUNT
    : defaultInstallmentsCount;

  const t = useLocale();
  const getPaymentScheduleTitles = useGetPaymentScheduleTitles();

  const initialValue = (price || INITIAL_PRICE).toString();
  const [inputValue, setValue] = useState(initialValue);

  const checkPrice = createNormalizeCheckingNumber(
    'Price argument is not a number',
    0
  );
  const isRawPriceValid = !!checkPrice(priceRaw);
  const isCalculationEnabled = !isRawPriceValid;

  useEffect(() => {
    if (!shouldRenderInstallmentsSchedule) {
      dispatch({
        type: ACTIONS.SET_TITLES,
        payload: [],
      });
      return;
    }

    dispatch({
      type: ACTIONS.SET_TITLES,
      payload: new Array(installmentsCount)
        .fill(null)
        .map((_n, i) => getPaymentScheduleTitles(i)),
    });
  }, [
    dispatch,
    getPaymentScheduleTitles,
    installmentsCount,
    shouldRenderInstallmentsSchedule,
  ]);

  const pricePart = getPriceSplitOf(
    Number(inputValue),
    currency,
    installmentsCount
  );

  const onChange = (val: string) => {
    setValue(val);
  };

  const initialPreparedPrice = priceRaw && isRawPriceValid ? priceRaw : price;
  const value = isCalculationEnabled
    ? Number(inputValue)
    : Number(initialPreparedPrice);

  const isPi4 = installmentsCount === 4;
  const paymentOptions = (
    <PaymentOptionsList list={isCalculationEnabled}>
      {country === 'KSA' && (
        <InstallmentItem
          key="three-payments"
          title={getPaymentText(INSTALLMENT_PLANS.three, t)}
          subtitle={t('paid_plans.no_interest_no_fees')}
          paymentsInfo={t(
            'paid_plans.payment_per_period',
            {
              price: getPriceSplitOf(value, currency, INSTALLMENT_PLANS.three),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          )}
          list={isCalculationEnabled}
        />
      )}

      {isRamadanDefaultViewExp && country === 'KSA' ? (
        [
          <InstallmentItem
            key="four-payments"
            title={getPaymentText(INSTALLMENT_PLANS.four, t)}
            subtitle={t('no_interest_processing_fee_may_apply')}
            paymentsInfo={t(
              'paid_plans.payment_per_period',
              {
                price: pricePart,
                period: t('period'),
              },
              {
                currency: () => <Currency currency={currency} />,
              }
            )}
            isGreenSubtitle={country !== 'KSA'}
            list={isCalculationEnabled}
          />,
          <InstallmentItem
            key="six-payments"
            title={getPaymentText(INSTALLMENT_PLANS.six, t)}
            subtitle={t('processing_fee_may_apply')}
            paymentsInfo={t(
              'paid_plans.payment_per_period',
              {
                price: pricePart,
                period: t('period'),
              },
              {
                currency: () => <Currency currency={currency} />,
              }
            )}
            noInterest={t('no_interest')}
            isLimitedTimeOffer
            isGreenSubtitle={country !== 'KSA'}
            list={isCalculationEnabled}
          />,
        ]
      ) : (
        <InstallmentItem
          key={`${installmentsCount}-payments`}
          title={getPaymentText(installmentsCount, t)}
          subtitle={
            country === 'KSA' && isPi4
              ? t('no_interest_processing_fee_may_apply')
              : t('paid_plans.no_interest_no_fees')
          }
          paymentsInfo={t(
            'paid_plans.payment_per_period',
            {
              price: pricePart,
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          )}
          isGreenSubtitle={country !== 'KSA'}
          list={isCalculationEnabled}
        />
      )}
    </PaymentOptionsList>
  );

  return (
    <>
      <PopupLearnMoreBackgroundHeader
        onClose={onClose}
        heroVariant={shouldUsePayLaterHeader ? 'pay-next-month' : 'default'}
        title={
          shouldUsePayLaterHeader
            ? t('pay_next_month')
            : getLearnMoreBackgroundTitle(currency, t)
        }
        subtitle={
          shouldUsePayLaterHeader
            ? t('no_interest_no_fees')
            : getSplitYourPurchaseText(installmentsCount, false, t, currency)
        }
      />

      {shouldRenderInstallmentsSchedule &&
        (isCalculationEnabled ? (
          <InstallmentsCalculatorCard
            input={
              <InstallmentsInput
                id="long-term-input"
                value={inputValue}
                onChange={onChange}
              />
            }
          >
            {paymentOptions}
          </InstallmentsCalculatorCard>
        ) : (
          paymentOptions
        ))}

      <PayNextMonthTab />

      <LearnMoreHowItWorks partnerCharges={partnerCharges} />

      {country === 'KSA' ? (
        <TrustBanner
          title={t('shariah_compliant_payments')}
          description={t('our_payment_options_meet_islamic_finance_standards')}
          icon="shariat-modern"
          iconClassName={styles.LearnMoreWithSchedule__BannerIcon}
        />
      ) : (
        <TrustBanner
          title={t('tabby_trusted.trusted_by_milions')}
          description={t('over_20_million_shoppers_pay_with_tabby')}
          icon="people"
        />
      )}

      <TrustBanner
        title={t('tabby_care.show_safely')}
        description={t('tabby_care.buyer_protection_included')}
        icon="tabby-care"
      />

      {onContinue && (
        <Button onClick={onContinue} testAttr="continue-shopping">
          {t('tabbySplit.continue_shopping')}
        </Button>
      )}

      <CreditCards country={country} fullWidth />
    </>
  );
};
